/**
 * Plan `2026-09-13-001-playbooks-qa-round4-plan.md`, U1 (R-L2, R-L3, R-D4;
 * Key Technical Decisions 3, 4) — pure, DOM-free leaves behind the sequence
 * card's new one-kebab header (`SequencesStep.tsx`) and reused verbatim by
 * `PlaybooksConfigPage.tsx`'s own sequence rename drawer (Decision 4: "so
 * Config's rename drawer can show the same line").
 *
 * Plan 2026-09-28-006 U4 adds the three pure leaves every in-play sequence
 * edit shares (the Sequences step, its three settings popovers, rename):
 * {@link SEQUENCE_UNAVAILABLE_NAME}, {@link SAVE_GROUP_FIRST} and
 * {@link sequenceSidebarTarget}. They live here, DOM-free, because the card
 * header is where all of those edits start.
 */
import { isArchivedInLibrary } from './play-ownership';
import type { PlaybooksSequenceSummary } from './schemas';
import type { UsageSidebarTarget } from './usage-sidebar-store';

/**
 * [R18][R20] The name a group's sequence shows when its library row is not
 * loaded or no longer exists — NEVER the raw id. (A Library piece an admin
 * archived still has its row on the play-scoped read, U1 (b), so it shows its
 * real name plus an "Archived in Library" tag instead.)
 */
export const SEQUENCE_UNAVAILABLE_NAME = 'Sequence unavailable';

/**
 * [R20] The one sentence an in-play edit shows when the group it came from
 * has no saved id yet (a group added this session that has not reached the
 * server): a per-group copy needs the group to exist first. No request is
 * sent.
 */
export const SAVE_GROUP_FIRST = 'Save this group first, then try again.';

/**
 * [R5][R6][R18] The usage sidebar's `target` for a sequence edit: the name
 * (never an id), who owns it (`owningPlayId` — `saveMenuOptions` picks the
 * owned-target menu from it) and whether it is a Library piece archived while
 * this play still uses it (③ hidden). `row` is the play-scoped library row;
 * `undefined` while it loads, which reads as a Library, unarchived piece.
 */
export function sequenceSidebarTarget(
  id: string,
  row: PlaybooksSequenceSummary | undefined,
): UsageSidebarTarget {
  return {
    kind: 'sequence',
    id,
    name: row?.name ?? SEQUENCE_UNAVAILABLE_NAME,
    owningPlayId: row?.owningPlayId ?? null,
    archivedInLibrary: row !== undefined && isArchivedInLibrary(row),
  };
}

export type SequenceCardMenuAction = 'change' | 'rename' | 'remove';

export interface SequenceCardMenuItem {
  action: SequenceCardMenuAction;
  label: string;
  danger?: boolean;
}

/**
 * L2/L3 — "Change sequence" + "Remove sequence" collapse into ONE kebab with
 * Change / Rename / Remove(danger), in that order. Order is stable — tests
 * and the render site both rely on it (`KebabMenu`'s own item order mirrors
 * the array it's given).
 *
 * Round-4 QA codex-review fix (batch 1, finding 1) — `canRename` (default
 * `true`, so every existing caller/test keeps its exact 3-item result)
 * omits the `rename` entry entirely. The render site (`SequencesStep.tsx`)
 * passes `false` while `sequenceSummary` (the library row Rename reads its
 * `name`/`usageCount` from) hasn't resolved yet: `NameDrawerField` seeds its
 * input once, keyed on `mapping.sequenceId`, so a late library response
 * never repairs a name that was seeded from the fallback — offering Rename
 * before the real name/usage-count exist risked saving the sequence's OWN
 * ID as its new name and skipping the shared-use warning entirely.
 */
export function getSequenceCardMenuItems(options?: { canRename?: boolean }): SequenceCardMenuItem[] {
  const canRename = options?.canRename ?? true;
  return [
    { action: 'change', label: 'Change sequence' },
    ...(canRename ? [{ action: 'rename' as const, label: 'Rename' }] : []),
    { action: 'remove', label: 'Remove sequence', danger: true },
  ];
}

/**
 * Plan 2026-09-28-006 U4 — shown on the DOORWAYS only (the playbook page and
 * Config's rename drawer). Inside a play a rename goes through the same
 * usage gate and Save menu as every other edit (rulebook R5/R6), so the
 * Sequences step passes nothing here in a play: the gate says it now.
 *
 * D4 (R-D4) — a rename from inside a play warns when the sequence is shared:
 * `usageCount` is the library summary's rung-count-across-active-playbook-heads
 * (`PlaybooksSequenceSummarySchema.usageCount`), already loaded wherever this
 * is called — no extra fetch (Decision 4: a rename is low-stakes and
 * non-version-producing, so a `staleTime: 0` usage check isn't worth the
 * round-trip). `null` for the common case (0 or 1 place) — the caller renders
 * nothing when this is `null`.
 */
export function renameWarningText(usageCount: number): string | null {
  if (usageCount <= 1) return null;
  return `Used in ${usageCount} places — renaming changes it everywhere.`;
}

// Playbooks QA round 5, R5-5 — `senderWarningText` used to live here (a
// plain inline warning, no gate, no fetch). Playbooks QA round 6, U7 (item
// 3) DELETES it: once a sender edit offers a real fork-or-update-everywhere
// CHOICE (`SequenceSenderPopover.tsx`'s new `saveWithGate`, the usage
// sidebar), a caption that only ever describes the "update everywhere"
// branch is misleading — replaced by `usage-gate.ts#usedInPlacesText`, the
// same discoverability caption every other usage badge/footer in this app
// already uses.
